import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// NotifyProvider — lightweight global toast system.
//
// Usage from any component inside the provider:
//
//   const { notifySuccess, notifyError, notifyInfo } = useNotify();
//   notifySuccess('Payment recorded');
//   notifyError('Could not save — check your connection');
//
// Toasts auto-dismiss after ~4s (success/info) or ~6s (errors).
// This is intentionally simple: no persistent queue, no undo — it's for
// confirmation and error feedback, not for critical audit-trail messaging.
// For cancel-window email toasts see components/Toast.jsx.
// ─────────────────────────────────────────────────────────────────────────────

const NotifyContext = createContext(null);

const typeStyles = {
  success: {
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    text: 'text-emerald-900',
    iconColor: 'text-emerald-600',
    Icon: CheckCircle,
    defaultDuration: 4000,
  },
  error: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-900',
    iconColor: 'text-red-600',
    Icon: AlertCircle,
    defaultDuration: 6000,
  },
  info: {
    bg: 'bg-slate-50',
    border: 'border-slate-200',
    text: 'text-slate-900',
    iconColor: 'text-slate-600',
    Icon: Info,
    defaultDuration: 4000,
  },
};

export function NotifyProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((type, message, opts = {}) => {
    if (!message) return null;
    const style = typeStyles[type] || typeStyles.info;
    const id = ++idRef.current;
    const duration = opts.duration ?? style.defaultDuration;
    setToasts((prev) => [...prev, { id, type, message, duration }]);
    if (duration > 0) {
      window.setTimeout(() => dismiss(id), duration);
    }
    return id;
  }, [dismiss]);

  const api = useMemo(() => ({
    notify: (msg, type = 'info', opts) => push(type, msg, opts),
    notifySuccess: (msg, opts) => push('success', msg, opts),
    notifyError: (msg, opts) => push('error', msg, opts),
    notifyInfo: (msg, opts) => push('info', msg, opts),
    dismiss,
  }), [push, dismiss]);

  return (
    <NotifyContext.Provider value={api}>
      {children}
      <div className="fixed bottom-6 right-6 z-[100] flex flex-col gap-2 items-end no-print pointer-events-none">
        {toasts.map((t) => {
          const style = typeStyles[t.type] || typeStyles.info;
          const Icon = style.Icon;
          return (
            <div
              key={t.id}
              className={`pointer-events-auto shadow-lg rounded-lg border ${style.bg} ${style.border} px-4 py-3 pr-10 flex items-start gap-3 min-w-[260px] max-w-[420px] relative`}
              role={t.type === 'error' ? 'alert' : 'status'}
            >
              <Icon size={18} className={`flex-shrink-0 mt-0.5 ${style.iconColor}`} />
              <div className={`text-sm ${style.text} whitespace-pre-line`}>{t.message}</div>
              <button
                onClick={() => dismiss(t.id)}
                className="absolute top-2 right-2 p-1 text-slate-400 hover:text-slate-700 rounded transition"
                aria-label="Dismiss"
              >
                <X size={14} />
              </button>
            </div>
          );
        })}
      </div>
    </NotifyContext.Provider>
  );
}

export function useNotify() {
  const ctx = useContext(NotifyContext);
  if (!ctx) {
    // Safe fallback during tests or if a component is rendered outside the
    // provider — doesn't crash, just logs and no-ops.
    return {
      notify: (msg) => { console.warn('[notify] (no provider):', msg); },
      notifySuccess: (msg) => { console.warn('[notify.success] (no provider):', msg); },
      notifyError: (msg) => { console.warn('[notify.error] (no provider):', msg); },
      notifyInfo: (msg) => { console.warn('[notify.info] (no provider):', msg); },
      dismiss: () => {},
    };
  }
  return ctx;
}

export default NotifyProvider;
